import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import ExitPassTicket from "../components/ExitPassTicket";
import { PassForm, PassCard, EmptyState, BelongingsSection, getStatusBadge, canEditPass, canCancelPass, emptyPass } from "../components/PassComponents";
import { Plus, LogOut, User, FileText, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffDashboard() {
  const { user, logout, getAuthHeaders, loading } = useAuth();
  const [exitPasses, setExitPasses] = useState([]);
  const [stats, setStats] = useState({});
  const [showNewPassDialog, setShowNewPassDialog] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingPass, setEditingPass] = useState(null);
  const [cancelPass, setCancelPass] = useState(null);
  const [cancelNote, setCancelNote] = useState("");
  const [newPass, setNewPass] = useState(emptyPass);
  const [myBelongings, setMyBelongings] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [passesRes, statsRes, belongingsRes] = await Promise.all([
        axios.get(`${API}/exit-passes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/dashboard/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/belongings/me`, { headers: getAuthHeaders() }),
      ]);
      setExitPasses(passesRes.data);
      setStats(statsRes.data);
      setMyBelongings(belongingsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const handleSubmitPass = async (e) => {
    e.preventDefault();
    if (!newPass.transport_type || !newPass.destination || !newPass.purpose ||
        !newPass.departure_date || !newPass.departure_time ||
        !newPass.arrival_date || !newPass.arrival_time) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/exit-passes`, newPass, { headers: getAuthHeaders() });
      toast.success("Exit pass request submitted!");
      setShowNewPassDialog(false);
      setNewPass(emptyPass);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit exit pass");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPass = async (e) => {
    e.preventDefault();
    if (!editingPass) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/exit-passes/${editingPass.id}`, {
        transport_type: editingPass.transport_type,
        transport_details: editingPass.transport_details,
        destination: editingPass.destination,
        purpose: editingPass.purpose,
        departure_date: editingPass.departure_date,
        departure_time: editingPass.departure_time,
        arrival_date: editingPass.arrival_date,
        arrival_time: editingPass.arrival_time,
      }, { headers: getAuthHeaders() });
      toast.success("Exit pass updated!");
      setEditingPass(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelPass = async () => {
    if (!cancelPass) return;
    if (!cancelNote.trim()) { toast.error("Please provide a reason"); return; }
    setSubmitting(true);
    try {
      await axios.delete(`${API}/exit-passes/${cancelPass.id}`, {
        headers: getAuthHeaders(), data: { cancel_note: cancelNote.trim() }
      });
      toast.success("Exit pass cancelled");
      setCancelPass(null); setCancelNote(""); fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;

  // Split passes: recent (active/pending/approved) vs history (used/cancelled/rejected)
  const activePasses = exitPasses.filter(p => ["pending", "approved"].includes(p.status));
  const recentPass = activePasses[0]; // Most recent active pass
  const historyPasses = exitPasses.filter(p => ["used", "cancelled", "rejected"].includes(p.status));

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-heading font-bold text-stone-800 text-sm">{user?.name}</h1>
              <p className="text-xs text-stone-500">{user?.employee_id} - {user?.department_name || "No Department"}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={logout} className="text-stone-500 hover:text-red-600 gap-2" data-testid="logout-btn">
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <Card className="bg-white border-stone-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-amber-600">{stats.my_pending_passes || 0}</p>
              <p className="text-xs text-stone-500 mt-1">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-stone-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-emerald-600">{stats.my_approved_passes || 0}</p>
              <p className="text-xs text-stone-500 mt-1">Approved</p>
            </CardContent>
          </Card>
        </div>

        <Dialog open={showNewPassDialog} onOpenChange={setShowNewPassDialog}>
          <DialogTrigger asChild>
            <Button className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-14 text-base shadow-lg shadow-primary/20 gap-2" data-testid="new-pass-btn">
              <Plus className="w-5 h-5" strokeWidth={2} /> New Exit Pass Request
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">New Exit Pass Request</DialogTitle>
              <DialogDescription>Fill in the details for your exit pass</DialogDescription>
            </DialogHeader>
            <PassForm pass={newPass} setPass={setNewPass} onSubmit={handleSubmitPass} submitting={submitting} btnLabel="Submit Request" />
          </DialogContent>
        </Dialog>

        {/* Recent/Active Exit Passes */}
        <div>
          <h2 className="font-heading font-bold text-stone-800 mb-3">Active Exit Passes</h2>
          {activePasses.length === 0 ? (
            <EmptyState icon={FileText} message="No active exit passes. Create a new request above." />
          ) : (
            <div className="space-y-3">
              {activePasses.map((pass) => (
                <PassCard key={pass.id} pass={pass} getStatusBadge={getStatusBadge}
                  onViewQR={() => { setSelectedPass(pass); setShowQRDialog(true); }}
                  onEdit={() => setEditingPass({ ...pass, purpose_type: pass.purpose })}
                  onCancel={() => { setCancelPass(pass); setCancelNote(""); }}
                  canEdit={canEditPass(pass)} canCancel={canCancelPass(pass)} />
              ))}
            </div>
          )}
        </div>

        {/* My Belongings */}
        <BelongingsSection belongings={myBelongings} testIdPrefix="my" />

        {/* Pass History - Collapsed */}
        {historyPasses.length > 0 && (
          <div>
            <button onClick={() => setShowHistory(!showHistory)} className="flex items-center justify-between w-full p-3 bg-stone-100 rounded-xl hover:bg-stone-200 transition-colors" data-testid="toggle-history-btn">
              <span className="font-heading font-semibold text-stone-700 text-sm">View Pass History ({historyPasses.length})</span>
              {showHistory ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
            </button>
            {showHistory && (
              <div className="space-y-3 mt-3">
                {historyPasses.map((pass) => (
                  <PassCard key={pass.id} pass={pass} getStatusBadge={getStatusBadge}
                    onViewQR={() => { setSelectedPass(pass); setShowQRDialog(true); }}
                    onEdit={() => {}} onCancel={() => {}} canEdit={false} canCancel={false} />
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Exit Pass QR Code</DialogTitle></DialogHeader>
          {selectedPass && <ExitPassTicket pass={selectedPass} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingPass} onOpenChange={(v) => !v && setEditingPass(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Edit Exit Pass</DialogTitle></DialogHeader>
          {editingPass && <PassForm pass={editingPass} setPass={setEditingPass} onSubmit={handleEditPass} submitting={submitting} btnLabel="Save Changes" />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelPass} onOpenChange={(v) => !v && setCancelPass(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Cancel Exit Pass</DialogTitle>
            <DialogDescription>{cancelPass?.destination} - {cancelPass?.departure_date}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Reason for cancellation *</Label>
              <Textarea value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} placeholder="Please provide a reason..." className="mt-2" rows={3} data-testid="cancel-note-input" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setCancelPass(null)}>Keep Pass</Button>
              <Button onClick={handleCancelPass} disabled={submitting || !cancelNote.trim()} data-testid="confirm-cancel-btn" className="flex-1 bg-red-500 hover:bg-red-600 text-white">
                {submitting ? "Cancelling..." : "Cancel Pass"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
